// src/components/dashboard/PrescriptionPage.tsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, Pill, Heart, ArrowLeft, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '@/services/api';
import { PrescriptionGrid } from './PrescriptionGrid';

// Interfaces matching the backend data structure
interface Medicine {
  medicine_id: string;
  matched_name?: string;
  name?: string;
  medicine_name?: string;
  category?: string;
  strength?: string;
  frequency?: string;
  route?: string;
  explanation?: string;
}

interface Vitals {
  bp_systolic?: number;
  bp_diastolic?: number;
  pulse?: number;
  spo2?: number;
  temperature?: number;
  weight?: number;
  height?: number;
  age?: number;
}

interface PrescriptionListItem {
  id: string;
  doctor_name?: string;
  hospital?: string;
  reason_grouping?: string;
  prescribed_date?: string;
}

interface PrescriptionDetail {
  prescription_id: string;
  doctor_name?: string;
  hospital?: string;
  chief_complaints?: string;
  prescribed_date?: string;
}

interface PrescriptionInsights {
  is_handwritten?: boolean;
  reason_grouping?: string;
}

type ViewType = 'grid' | 'detail';

export const PrescriptionPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewType>('grid');
  const [prescriptionList, setPrescriptionList] = useState<PrescriptionListItem[]>([]);
  const [selectedPrescription, setSelectedPrescription] = useState<any>(null);
  const [expandedMedicineId, setExpandedMedicineId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Always reset to grid view when component mounts/remounts
    setCurrentView('grid');
    setSelectedPrescription(null);
    setExpandedMedicineId(null);

    loadPrescriptionList();
  }, []);

  const loadPrescriptionList = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getUserPrescriptions(50);

      if (response.success && response.data && response.data.prescriptions) {
        setPrescriptionList(response.data.prescriptions);
      } else {
        setPrescriptionList([]);
      }
    } catch (err: any) {
      console.error('Failed to load prescriptions:', err);
      if (err.message !== 'UNAUTHORIZED') {
        setError(err.message || 'Failed to load prescriptions');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPrescriptionDetail = async (prescriptionId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getPrescriptionById(prescriptionId);

      if (response.success && response.data) {
        setSelectedPrescription(response.data);
        setCurrentView('detail');
        setExpandedMedicineId(null); // Reset expanded medicine
      }
    } catch (err: any) {
      console.error('Failed to load prescription details:', err);
      if (err.message !== 'UNAUTHORIZED') {
        setError(err.message || 'Failed to load prescription details');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMedicineExpansion = (medicineId: string) => {
    // If clicking the same medicine, collapse it. Otherwise, expand the new one (collapsing any previously expanded)
    setExpandedMedicineId(prevId => {
      if (prevId === medicineId) {
        // Clicking on already expanded medicine - collapse it
        return null;
      } else {
        // Clicking on a different medicine - expand it (auto-collapses previous)
        return medicineId;
      }
    });
  };

  const handleBackToGrid = () => {
    setCurrentView('grid');
    setSelectedPrescription(null);
    setExpandedMedicineId(null);
  };

  // Grid View - List of all prescriptions
  const renderGridView = () => {
    if (loading) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          fontFamily: 'var(--font-primary)',
          padding: '20px'
        }}>
          <p style={{ color: 'var(--color-muted)' }}>Loading prescriptions...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          fontFamily: 'var(--font-primary)',
          gap: 'var(--space-4)',
          padding: '20px'
        }}>
          <p style={{ color: 'var(--color-error)', textAlign: 'center' }}>Error: {error}</p>
          <button
            onClick={loadPrescriptionList}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-background)',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (prescriptionList.length === 0) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          fontFamily: 'var(--font-primary)',
          gap: 'var(--space-4)',
          padding: 'clamp(20px, 5vw, 40px)'
        }}>
          <div style={{
            fontSize: 'clamp(48px, 10vw, 64px)',
            marginBottom: 'var(--space-4)'
          }}>📋</div>
          <h2 style={{
            fontSize: 'clamp(20px, 4vw, 28px)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--color-text)',
            margin: 0,
            textAlign: 'center'
          }}>
            No Prescriptions Available
          </h2>
          <p style={{
            color: 'var(--color-muted)',
            textAlign: 'center',
            maxWidth: '500px',
            fontSize: 'clamp(14px, 2.5vw, 16px)',
            padding: '0 20px'
          }}>
            Upload a prescription from your reports to view medication details and instructions here.
          </p>
        </div>
      );
    }

    return (
      <PrescriptionGrid
        prescriptions={prescriptionList}
        onSelectPrescription={loadPrescriptionDetail}
      />
    );
  };

  // Detail View - Full prescription details
  const renderDetailView = () => {
    if (loading) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          fontFamily: 'var(--font-primary)',
          padding: '20px'
        }}>
          <p style={{ color: 'var(--color-muted)' }}>Loading prescription details...</p>
        </div>
      );
    }

    if (!selectedPrescription) return null;

    const { prescription, vitals, medicines, insights } = selectedPrescription;

    return (
      <div style={{
        fontFamily: 'var(--font-primary)',
        padding: 'clamp(20px, 5vw, 40px)',
        maxWidth: '900px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Back Button */}
        <button
          onClick={handleBackToGrid}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--color-primary)',
            fontSize: 'clamp(13px, 2.5vw, 14px)',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: 'clamp(16px, 3vw, 24px)',
            padding: '8px 0'
          }}
        >
          <ArrowLeft size={20} />
          Back to Prescriptions
        </button>

        {/* Handwritten Warning Banner - from prescription_insights */}
        {insights?.is_handwritten && (
          <div style={{
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '2px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px',
            padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 20px)',
            marginBottom: 'clamp(20px, 4vw, 32px)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <AlertCircle size={24} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <h3 style={{
                fontSize: 'clamp(14px, 2.5vw, 15px)',
                fontWeight: 600,
                color: 'var(--color-text)',
                margin: '0 0 4px 0'
              }}>
                Handwritten Prescription
              </h3>
              <p style={{
                fontSize: 'clamp(12px, 2vw, 13px)',
                color: 'var(--color-secondary)',
                margin: 0,
                lineHeight: 1.6
              }}>
                This extraction is from a handwritten prescription. Please verify against the original and update if necessary.
              </p>
            </div>
          </div>
        )}

        {/* Card 1: Prescription Snapshot */}
        <div style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: 'clamp(16px, 4vw, 24px)',
          marginBottom: 'clamp(16px, 3vw, 24px)'
        }}>
          <h2 style={{
            fontSize: 'clamp(13px, 2.5vw, 14px)',
            fontWeight: 700,
            color: 'var(--color-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            margin: '0 0 clamp(12px, 3vw, 20px) 0'
          }}>
            Prescription Snapshot
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {prescription.doctor_name && (
              <div>
                <div style={{
                  fontSize: 'clamp(11px, 2vw, 12px)',
                  color: 'var(--color-muted)',
                  marginBottom: '4px'
                }}>
                  Dr Name
                </div>
                <div style={{
                  fontSize: 'clamp(15px, 3vw, 16px)',
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  wordBreak: 'break-word'
                }}>
                  Dr. {prescription.doctor_name}
                </div>
              </div>
            )}

            {prescription.hospital && (
              <div>
                <div style={{
                  fontSize: 'clamp(11px, 2vw, 12px)',
                  color: 'var(--color-muted)',
                  marginBottom: '4px'
                }}>
                  Hospital Name
                </div>
                <div style={{
                  fontSize: 'clamp(15px, 3vw, 16px)',
                  color: 'var(--color-text)',
                  wordBreak: 'break-word'
                }}>
                  {prescription.hospital}
                </div>
              </div>
            )}

            {prescription.chief_complaints && (
              <div>
                <div style={{
                  fontSize: 'clamp(11px, 2vw, 12px)',
                  color: 'var(--color-muted)',
                  marginBottom: '4px'
                }}>
                  Chief Complaints
                </div>
                <div style={{
                  fontSize: 'clamp(15px, 3vw, 16px)',
                  color: 'var(--color-text)',
                  wordBreak: 'break-word'
                }}>
                  {prescription.chief_complaints}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 2: Vitals and Examination */}
        {vitals && Object.keys(vitals).length > 0 && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            padding: 'clamp(16px, 4vw, 24px)',
            marginBottom: 'clamp(16px, 3vw, 24px)'
          }}>
            <h2 style={{
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: 700,
              color: 'var(--color-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: '0 0 clamp(12px, 3vw, 20px) 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Activity size={16} />
              Vitals and Examination
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))',
              gap: 'clamp(12px, 2.5vw, 16px)'
            }}>
              {vitals.bp_systolic && vitals.bp_diastolic && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    BP
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.bp_systolic} / {vitals.bp_diastolic}
                  </div>
                </div>
              )}

              {vitals.pulse && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    Pulse
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.pulse} bpm
                  </div>
                </div>
              )}

              {vitals.spo2 && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    SpO2
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.spo2}%
                  </div>
                </div>
              )}

              {vitals.temperature && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    Temperature
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.temperature} °F
                  </div>
                </div>
              )}

              {vitals.weight && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    Weight
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.weight} kg
                  </div>
                </div>
              )}

              {vitals.height && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    Height
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.height} cm
                  </div>
                </div>
              )}

              {vitals.age && (
                <div>
                  <div style={{
                    fontSize: 'clamp(11px, 2vw, 12px)',
                    color: 'var(--color-muted)',
                    marginBottom: '4px'
                  }}>
                    Age
                  </div>
                  <div style={{
                    fontSize: 'clamp(15px, 3vw, 16px)',
                    fontWeight: 600,
                    color: 'var(--color-text)'
                  }}>
                    {vitals.age} years
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card 3: Medicines with Accordion */}
        {medicines && medicines.length > 0 && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            padding: 'clamp(16px, 4vw, 24px)',
            marginBottom: 'clamp(16px, 3vw, 24px)'
          }}>
            <h2 style={{
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: 700,
              color: 'var(--color-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: '0 0 clamp(12px, 3vw, 20px) 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Pill size={16} />
              Medicines
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {medicines.map((medicine: Medicine, index: number) => {
                // Determine display name with priority: matched_name > name > medicine_name
                const displayName = medicine.matched_name || medicine.name || medicine.medicine_name || 'Unknown Medicine';

                // Use displayName as fallback ID if medicine_id is not available
                const medicineId = medicine.medicine_id || displayName || `medicine-${index}`;
                const isExpanded = expandedMedicineId === medicineId;
                const hasDetails = !!(medicine.explanation || medicine.category);

                return (
                  <div
                    key={medicineId}
                    style={{
                      backgroundColor: 'var(--color-background)',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Medicine Header - Always Visible */}
                    <div
                      onClick={() => hasDetails && toggleMedicineExpansion(medicineId)}
                      style={{
                        padding: 'clamp(12px, 3vw, 16px)',
                        cursor: hasDetails ? 'pointer' : 'default',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (hasDetails) {
                          e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '4px',
                        gap: '8px'
                      }}>
                        <div style={{
                          fontSize: 'clamp(14px, 2.5vw, 15px)',
                          fontWeight: 600,
                          color: 'var(--color-text)',
                          wordBreak: 'break-word',
                          flex: 1
                        }}>
                          {displayName}
                        </div>
                        {hasDetails && (
                          <div style={{
                            color: 'var(--color-primary)',
                            flexShrink: 0
                          }}>
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: 'clamp(12px, 2vw, 13px)',
                        color: 'var(--color-text)',
                        opacity: 0.8,
                        wordBreak: 'break-word'
                      }}>
                        {[medicine.strength, medicine.frequency, medicine.route, medicine.category].filter(Boolean).join(' | ')}
                      </div>
                    </div>

                    {/* Expandable Medicine Details */}
                    {isExpanded && hasDetails && (
                      <div style={{
                        padding: '0 clamp(12px, 3vw, 16px) clamp(12px, 3vw, 16px) clamp(12px, 3vw, 16px)',
                        borderTop: '1px solid var(--color-border)'
                      }}>
                        {medicine.category && (
                          <div style={{ marginTop: '16px' }}>
                            <div style={{
                              fontSize: 'clamp(11px, 2vw, 12px)',
                              color: 'var(--color-muted)',
                              marginBottom: '4px',
                              fontWeight: 600
                            }}>
                              Category
                            </div>
                            <div style={{
                              fontSize: 'clamp(13px, 2.5vw, 14px)',
                              color: 'var(--color-text)',
                              wordBreak: 'break-word'
                            }}>
                              {medicine.category}
                            </div>
                          </div>
                        )}

                        {medicine.explanation && (
                          <div style={{ marginTop: '16px' }}>
                            <div style={{
                              fontSize: 'clamp(11px, 2vw, 12px)',
                              color: 'var(--color-muted)',
                              marginBottom: '8px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Understanding your Medicine
                            </div>
                            <div style={{
                              fontSize: 'clamp(13px, 2.5vw, 14px)',
                              color: 'var(--color-text)',
                              lineHeight: 1.7,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {medicine.explanation}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Card 4: heal Insight - Revamped for better visibility */}
        <div style={{
          backgroundColor: 'var(--color-primary)',
          borderRadius: '12px',
          padding: 'clamp(24px, 5vw, 32px) clamp(16px, 4vw, 24px)',
          textAlign: 'center',
          color: 'var(--color-background)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
          border: '2px solid var(--color-primary)'
        }}>
          <div style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Heart size={40} fill="var(--color-background)" color="var(--color-background)" />
          </div>
          <h3 style={{
            fontSize: 'clamp(18px, 4vw, 22px)',
            fontWeight: 700,
            marginBottom: '12px',
            margin: 0,
            color: 'var(--color-background)'
          }}>
            You're on the right track!
          </h3>
          <p style={{
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            lineHeight: 1.7,
            margin: '12px 0 0 0',
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto',
            color: 'var(--color-background)',
            opacity: 0.95
          }}>
            Following this schedule helps recovery. Keep up the great work with your health!
          </p>
        </div>
      </div>
    );
  };

  // Render based on current view
  if (currentView === 'grid') {
    return renderGridView();
  } else if (currentView === 'detail') {
    return renderDetailView();
  }

  return null;
};
